import { graphql } from '@apollo/client/react/hoc';
import invariant from 'invariant';
import * as React from 'react';

import { mountModal } from 'lib/modal';
import ADD_DEVICE_TO_USER_MUTATION from 'users/queries/AddDeviceToUserMutation.gql';
import assertions from 'lib/assertions';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import CONNECT_USER_ADDING_NUMBER_QUERY from 'users/queries/ConnectUserAddingNumberQuery.gql';
import CONTACTABILITY_EDUCATION_QUERY from 'chat/queries/contactabilityEducationQuery.gql';
import Dialog from 'components/Dialog';
import FormElement from 'components/FormElement';
import Link from 'components/Link';
import PhoneCallModal from 'phoneCalls/components/PhoneCallModal';
import PhoneNumberMaskedInput from 'components/PhoneNumberMaskedInput';
import RemindAPI from 'lib/RemindAPI';
import Row from 'components/Row';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';

import type { ConnectUserAddingNumber_me_connectedUsers } from 'users/queries/__generated__/ConnectUserAddingNumber';

import './styles/UsersAddNumberToUserModal.scss';

type Props = {
  onRequestHide: () => void;
  addDeviceToUser: (userId: Backbone$Id, address: string) => Promise<any>;
  userName: string;
  subtext: string;
  submitLabel: string;
  userId: Backbone$Id;
  userUuid: string;
  startCall?: Function;
  onCancel: Function;
};

type State = {
  errorMessage: string | undefined | null;
  existingUser: ConnectUserAddingNumber_me_connectedUsers | undefined | null;
  submitting: boolean;
  phoneNumber: string;
};

class UsersAddNumberToUserModal extends React.Component<Props, State> {
  state: State = {
    errorMessage: null,
    existingUser: null,
    submitting: false,
    phoneNumber: '',
  };

  callExistingUser = () => {
    const { existingUser } = this.state;
    invariant(existingUser, 'callExistingUser called with no existing user');
    // @ts-expect-error ts-migrate(2533) FIXME: Object is possibly 'null' or 'undefined'.
    mountModal(<PhoneCallModal calleeUuid={existingUser.uuid} />, this);
    this.props.onRequestHide();
  };

  submitForm = () => {
    const { userId, userUuid, addDeviceToUser, onRequestHide, startCall } = this.props;
    const { phoneNumber } = this.state;
    this.setState({ submitting: true, errorMessage: null, existingUser: null });
    addDeviceToUser(userId, 'sms://' + phoneNumber)
      .then(
        ({ data }) => {
          const { error } = data.addDeviceToUser;
          if (error) {
            if (error.code === 'already_taken_connected') {
              RemindAPI.execute({
                query: CONNECT_USER_ADDING_NUMBER_QUERY,
                variables: { smsAddresses: [phoneNumber] },
              }).then(
                (res) => {
                  // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
                  const users = res.me.connectedUsers;
                  this.setState({
                    existingUser: users[0],
                    errorMessage: error.message,
                    submitting: false,
                  });
                },
                () => {
                  this.setState({ errorMessage: error.message, submitting: false });
                  ToastActions.showErrorToast(error.message);
                }
              );
            } else {
              const message =
                error.code === 'validation_error' ? error.fieldErrors[0].message : error.message;
              ToastActions.showErrorToast(message);
              this.setState({ submitting: false, errorMessage: message });
            }
          } else if (startCall) {
            onRequestHide();
            startCall(userUuid);
            ToastActions.showSuccessToast(`Added a number to ${this.props.userName}`);
          } else {
            onRequestHide();
            ToastActions.showSuccessToast(`Added a number to ${this.props.userName}`);
          }
        },
        () => {
          ToastActions.showErrorToast('Error adding device to user');
          this.setState({ submitting: false });
        }
      )
      .catch((e) => {
        console.error('Error after adding device to user', e);
        ToastActions.showErrorToast('Error adding device to user');
        this.props.onRequestHide();
      });
  };

  setStateField = (name: string, value: any) => {
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ [x: string]: any; }' is not as... Remove this comment to see the full error message
    this.setState({ [name]: value });
  };

  render() {
    const { subtext, submitLabel, userName, startCall } = this.props;
    const { submitting, errorMessage, existingUser, phoneNumber } = this.state;
    return (
      <Dialog
        title={`What is ${userName}'s mobile number?`}
        subtext={subtext}
        size="small"
        onDialogDismiss={this.props.onCancel}
        onRequestHide={this.props.onRequestHide}
        hideCloseButton
      >
        <BoxBody className="UsersAddNumberToUserModal">
          <FormElement
            label="Phone number"
            labelHidden
            // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
            showErrorAfterBlur
            validate={(e) => {
              assertions.number(e.raw, 'Invalid phone number.');
              assertions.completePhoneNumber(e.raw, 'Invalid phone number.');
            }}
            onValidChange={(i) => this.setStateField('phoneNumber', i.raw)}
            onInvalidChange={() => this.setStateField('phoneNumber', undefined)}
            element={PhoneNumberMaskedInput}
            elementProps={{
              placeholder: '(555) 555-5555',
              className: errorMessage && 'UsersAddNumberToUserModal-input-error',
            }}
          />
          {existingUser && (
            <Row>
              <Text align="center" size="xsmall" color="apple">
                {errorMessage}.
                {startCall && (
                  <Link data-test="existing-user" block onClick={this.callExistingUser}>
                    Call {existingUser.name}
                  </Link>
                )}
              </Text>
            </Row>
          )}
          <Button
            onClick={this.submitForm}
            disabled={submitting || !phoneNumber}
            type="submit"
            data-test="submit-button"
          >
            {submitLabel}
          </Button>
        </BoxBody>
      </Dialog>
    );
  }
}

const withMutations = graphql(ADD_DEVICE_TO_USER_MUTATION, {
  props({ mutate, ownProps }) {
    return {
      addDeviceToUser(userId: Backbone$Id, address: string) {
        const input = { userId, address };
        // @ts-expect-error ts-migrate(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        return mutate({
          variables: { input },
          refetchQueries: [
            {
              query: CONTACTABILITY_EDUCATION_QUERY,
              // @ts-expect-error ts-migrate(2339) FIXME: Property 'userUuid' does not exist on type '{}'.
              variables: { uuid: ownProps.userUuid },
            },
          ],
        });
      },
    };
  },
});

export { UsersAddNumberToUserModal };

// @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'typeof UsersAddNumberToUserModal... Remove this comment to see the full error message
export default withMutations(UsersAddNumberToUserModal);
